import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
   AreaChart,
   Area,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
} from 'recharts';
import { colors } from '@/constants/colors';
import { getDeviceWanTraffic, type ApiVendoDevice } from '@/api/vendo';
import { isReportStale } from './deviceDiagText';

/**
 * "Is there internet, and is anything using it" — on the Internet (WAN) tab.
 *
 * Ray, 2026-08-26: today the only way an operator finds out their internet is
 * down is that a customer complains. He pointed at a competitor's panel that
 * draws a live traffic graph. That one runs ON the router and can sample every
 * second; ours lives in the cloud, so:
 *
 *   - the VERDICT (internet up / down) comes from the router's own check, which
 *     it already sends on every heartbeat. It is a real TLS handshake with a
 *     public resolver, so the modem's own "no internet" page cannot fake it.
 *   - the GRAPH is polled straight off the router, every 2s, and ONLY while
 *     this tab is open. A heartbeat is one point a minute and cannot draw it.
 *
 * WHAT IT MUST NEVER DO IS GUESS. A rate needs two samples over a known gap; a
 * blank space where a number would be is honest, "0 Mbps" is not — to the
 * person reading it, that says the internet is fine and nobody is using it.
 * Every path here that cannot produce a real number produces nothing instead.
 */

// One second. With the history frozen there is nothing to re-animate, so the
// only cost of a faster poll is the poll itself — and the endpoint it hits reads
// two counter files. Doubling the rate is what turns a visible step every two
// seconds into a line that travels: each reading now moves the plot by less than
// one percent of its width.
const POLL_MS = 1000;
/** Two minutes of history at POLL_MS.
 *
 *  It was five, and the plot spent its first five minutes three-quarters empty
 *  — a chart that looks broken until you have stared at it long enough. Two
 *  minutes fills the frame while an operator is still looking at it, and is
 *  still long enough that a spike has context either side of it. */
const MAX_POINTS = 120;

interface Point {
   t: number;
   /** Clock time, for the axis. Formatted once here rather than per render. */
   label: string;
   rxBps: number;
   txBps: number;
}

interface Props {
   deviceId: string;
   /** The router's own internet verdict, off the heartbeat. */
   diag?: NonNullable<ApiVendoDevice['router']>['diag'];
   /** When the router last spoke at all — an old verdict must not read as now. */
   lastSeenAt?: string | null;
   /** The heartbeat's copy, shown until the first live poll lands. */
   fallback?: NonNullable<ApiVendoDevice['router']>['wanTraffic'];
}

/** The next round number above a value: 1, 2 or 5 times a power of ten.
 *  Round ceilings give round gridlines — "500 kbps", not "473 kbps". */
export function niceCeiling(v: number): number {
   if (!Number.isFinite(v) || v <= 0) return 100_000;
   // The rounding IS the headroom — padding the value first and THEN rounding
   // compounds the two and throws away half the plot: a 473 kbps peak came out
   // at a 1 Mbps ceiling. Round up to the next 1/2/5, and only when the peak
   // lands exactly on one take the step above it, so a line never runs along
   // the very top edge.
   const mag = 10 ** Math.floor(Math.log10(v));
   const steps = [1, 2, 5, 10];
   for (let i = 0; i < steps.length; i += 1) {
      const c = steps[i] * mag;
      if (c > v) return c;
      if (c === v) return (steps[i + 1] ?? 10) * mag;
   }
   return 10 * mag;
}

/** Bits/sec → the unit an operator recognises from every other router panel. */
export function formatBps(bps: number | null | undefined): string | null {
   if (bps === null || bps === undefined || !Number.isFinite(bps) || bps < 0) {
      return null;
   }
   if (bps < 1000) return `${Math.round(bps)} bps`;
   if (bps < 1_000_000) return `${(bps / 1000).toFixed(bps < 10_000 ? 1 : 0)} kbps`;
   return `${(bps / 1_000_000).toFixed(bps < 10_000_000 ? 2 : 1)} mbps`;
}

/** Cumulative bytes → the "Total: ↓5.74 GB" line. */
export function formatBytes(b: number | null | undefined): string | null {
   if (b === null || b === undefined || !Number.isFinite(b) || b < 0) return null;
   if (b < 1024) return `${Math.round(b)} B`;
   if (b < 1024 ** 2) return `${(b / 1024).toFixed(0)} KB`;
   if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
   return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

/**
 * The internet verdict, and how much it is worth trusting.
 *
 * Three states, not two. "unknown" is a real answer — an older image does not
 * report this at all, and a router we have not heard from in a while may have
 * been offline the whole time. Ray caught exactly this shape of bug on the
 * Connection tab: a report that was true when it was sent and a lie by the time
 * anyone read it.
 */
export function internetVerdict(
   inet: string | null | undefined,
   lastSeenAt: string | null | undefined,
   now: Date = new Date()
): 'up' | 'down' | 'unknown' {
   if (isReportStale(lastSeenAt, now)) return 'unknown';
   if (inet === 'up') return 'up';
   if (inet === 'down') return 'down';
   return 'unknown';
}

const WanTrafficCard: React.FC<Props> = ({
   deviceId,
   diag,
   lastSeenAt,
   fallback,
}) => {
   const [points, setPoints] = useState<Point[]>([]);
   const [totals, setTotals] = useState<{ rx: number; tx: number } | null>(null);
   const [live, setLive] = useState<boolean | null>(null);
   const prev = useRef<{ t: number; rx: number; tx: number; uptime: number } | null>(
      null
   );

   useEffect(() => {
      let stopped = false;
      let timer: ReturnType<typeof setTimeout>;

      const tick = async () => {
         try {
            const s = await getDeviceWanTraffic(deviceId);
            if (stopped) return;
            if (!s.available) {
               setLive(false);
               prev.current = null;
            } else if (s.source === 'live') {
               setLive(true);
               setTotals({ rx: s.rxBytes ?? 0, tx: s.txBytes ?? 0 });
               const now = Date.now();
               const p = prev.current;
               // A rate needs two readings and the gap between them. The FIRST
               // poll can only ever produce a total, never a speed.
               if (p) {
                  const dt = (now - p.t) / 1000;
                  const rebooted = (s.uptime ?? 0) < p.uptime;
                  const rx = (s.rxBytes ?? 0) - p.rx;
                  const tx = (s.txBytes ?? 0) - p.tx;
                  // A restart resets the counters. Skip that one interval
                  // rather than draw the whole history as a single huge spike
                  // (or, worse, a negative).
                  if (dt >= 1 && !rebooted && rx >= 0 && tx >= 0) {
                     setPoints((old) =>
                        [
                           ...old,
                           {
                              t: now,
                              label: new Date(now).toLocaleTimeString([], {
                                 hour: '2-digit',
                                 minute: '2-digit',
                                 second: '2-digit',
                              }),
                              rxBps: (rx * 8) / dt,
                              txBps: (tx * 8) / dt,
                           },
                        ].slice(-MAX_POINTS)
                     );
                  }
               }
               prev.current = {
                  t: now,
                  rx: s.rxBytes ?? 0,
                  tx: s.txBytes ?? 0,
                  uptime: s.uptime ?? 0,
               };
            } else {
               // Heartbeat fallback: up to a minute old. Good enough for the
               // totals and the router's own rate, never plotted as if it were
               // this second.
               setLive(false);
               setTotals({ rx: s.rxBytes ?? 0, tx: s.txBytes ?? 0 });
               prev.current = null;
            }
         } catch {
            if (!stopped) setLive(false);
         }
         if (!stopped) timer = setTimeout(tick, POLL_MS);
      };

      tick();
      return () => {
         stopped = true;
         clearTimeout(timer);
      };
   }, [deviceId]);

   const verdict = internetVerdict(diag?.inet, lastSeenAt);

   // Always MAX_POINTS wide, blank-padded at the front. Without this the x-axis
   // stretches as history builds, so every new reading nudged all the earlier
   // ones sideways — the second thing that read as stuttering. Now the window is
   // a fixed frame and the line travels across it.
   const series = useMemo(() => {
      const pad = MAX_POINTS - points.length;
      if (pad <= 0) return points;
      return [
         ...Array.from({ length: pad }, (_, i) => ({
            t: -pad + i,
            label: '',
            rxBps: null as number | null,
            txBps: null as number | null,
         })),
         ...points,
      ];
   }, [points]);

   const latest = points[points.length - 1];
   const inNow = live && latest ? formatBps(latest.rxBps) : formatBps(fallback?.rxBps);
   const outNow = live && latest ? formatBps(latest.txBps) : formatBps(fallback?.txBps);

   const totalIn = formatBytes(totals?.rx ?? fallback?.rxBytes);
   const totalOut = formatBytes(totals?.tx ?? fallback?.txBytes);

   // One scale for both series, so the two areas can be read against each
   // other. A floor keeps an idle line flat along the bottom instead of
   // magnifying a trickle of background traffic into a mountain range.
   //
   // ⚠️ IT HAS TO HOLD STILL. Recomputing the exact peak every two seconds
   // rescaled the whole plot on every tick — the line stayed put and the axis
   // moved under it, which is most of what read as "laggy". So the ceiling
   // snaps to a round number (1, 2 or 5 × a power of ten) and only changes when
   // the data genuinely leaves the current one: it grows when a peak passes 90%
   // of it, and shrinks only once everything on screen fits under half of it.
   // Between those, the axis is nailed down and only the line moves.
   const peak = useMemo(
      () => points.reduce((a, p) => Math.max(a, p.rxBps, p.txBps), 0),
      [points]
   );
   const [ceiling, setCeiling] = useState(100_000);
   useEffect(() => {
      setCeiling((cur) => {
         if (peak > cur * 0.9) return niceCeiling(peak);
         if (peak > 0 && peak < cur * 0.5) return Math.max(niceCeiling(peak), 100_000);
         return cur;
      });
   }, [peak]);

   return (
      <Wrap>
         <Row>
            <Verdict $kind={verdict}>
               {verdict === 'up' && <>Internet is working</>}
               {verdict === 'down' && <>No internet</>}
               {verdict === 'unknown' && <>Internet not known</>}
            </Verdict>
            <Stats>
               <Stat>
                  <Dot $c={colors.RED} />
                  <StatLabel>IN</StatLabel>
                  <StatValue>{inNow ?? '—'}</StatValue>
               </Stat>
               <Stat>
                  <Dot $c={colors.BLUE} />
                  <StatLabel>OUT</StatLabel>
                  <StatValue>{outNow ?? '—'}</StatValue>
               </Stat>
            </Stats>
         </Row>

         <ChartBox>
            <ResponsiveContainer width="100%" height={200}>
               <AreaChart
                  data={series}
                  margin={{ top: 5, right: 12, left: 0, bottom: 0 }}
               >
                  <defs>
                     <linearGradient id="wanInGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={colors.RED} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={colors.RED} stopOpacity={0.02} />
                     </linearGradient>
                     <linearGradient id="wanOutGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={colors.BLUE} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={colors.BLUE} stopOpacity={0.02} />
                     </linearGradient>
                  </defs>
                  <CartesianGrid
                     strokeDasharray="3 3"
                     stroke={colors.GRAY13}
                     vertical={false}
                  />
                  <XAxis
                     dataKey="label"
                     tick={{ fontSize: 11, fill: colors.GRAY5 }}
                     axisLine={false}
                     tickLine={false}
                     minTickGap={48}
                  />
                  <YAxis
                     tick={{ fontSize: 11, fill: colors.GRAY5 }}
                     axisLine={false}
                     tickLine={false}
                     width={64}
                     domain={[0, ceiling]}
                     tickFormatter={(v: number) => formatBps(v) ?? ''}
                  />
                  <Tooltip
                     contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${colors.GRAY13}`,
                        fontSize: 12,
                     }}
                     labelFormatter={(l) => `at ${l}`}
                     formatter={(v, name) =>
                        [formatBps(Number(v)) ?? '—', String(name)] as [string, string]
                     }
                  />
                  <Area
                     type="monotone"
                     dataKey="rxBps"
                     name="IN"
                     stroke={colors.RED}
                     strokeWidth={2}
                     fill="url(#wanInGrad)"
                     connectNulls={false}
                     dot={false}
                     activeDot={{ r: 3 }}
                     // ⚠️ NO ANIMATION, and this is deliberate.
                     //
                     // Ray, 2026-08-27: "the previous wave is still animating".
                     // Exactly right. Every reading shifts the whole series one
                     // slot left, so Recharts re-tweens EVERY point — a burst
                     // from two minutes ago carries on wobbling for as long as
                     // it is on screen. Animating it was making the chart less
                     // truthful, not smoother: history is finished and should
                     // sit still.
                     //
                     // With the scale pinned and the window a fixed width, the
                     // only thing that changes between frames is one new point
                     // at the right-hand edge, which is what a live monitor is
                     // supposed to look like.
                     isAnimationActive={false}
                  />
                  <Area
                     type="monotone"
                     dataKey="txBps"
                     name="OUT"
                     stroke={colors.BLUE}
                     strokeWidth={2}
                     fill="url(#wanOutGrad)"
                     connectNulls={false}
                     dot={false}
                     activeDot={{ r: 3 }}
                     isAnimationActive={false}
                  />
               </AreaChart>
            </ResponsiveContainer>
         </ChartBox>

         <Foot>
            {points.length < 2 && live !== false && <span>Measuring…</span>}
            {live === false && (
               <span>
                  {verdict === 'down'
                     ? 'Not reading the router right now — it has no internet.'
                     : 'Not reading the router right now.'}
               </span>
            )}
            {(totalIn || totalOut) && (
               <Totals>
                  Since the router last restarted: ↓ {totalIn ?? '—'} · ↑{' '}
                  {totalOut ?? '—'}
               </Totals>
            )}
         </Foot>

         <Note>
            This is how much is flowing right now, not how fast your ISP line is.
            With nobody on the WiFi it sits near zero even on a perfect line.
         </Note>
      </Wrap>
   );
};

export default WanTrafficCard;

const Wrap = styled.div`
   border: 1px solid var(--app-border);
   border-radius: 10px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const Row = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const Verdict = styled.strong<{ $kind: 'up' | 'down' | 'unknown' }>`
   font-size: 14px;
   color: ${({ $kind }) =>
      $kind === 'up'
         ? colors.GREEN
         : $kind === 'down'
           ? colors.RED
           : colors.GRAY4};
`;

const Stats = styled.div`
   display: flex;
   gap: 16px;
`;

const Stat = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
`;

const Dot = styled.span<{ $c: string }>`
   width: 9px;
   height: 9px;
   border-radius: 2px;
   background: ${({ $c }) => $c};
`;

const StatLabel = styled.span`
   font-size: 11px;
   letter-spacing: 0.04em;
   color: ${colors.GRAY4};
`;

const StatValue = styled.span`
   font-size: 14px;
   font-variant-numeric: tabular-nums;
`;

const ChartBox = styled.div`
   width: 100%;
   /* Recharts needs a laid-out parent; the container carries the height. */
   min-height: 200px;
`;

const Foot = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 10px;
   flex-wrap: wrap;
   font-size: 12px;
   color: ${colors.GRAY4};
`;

const Totals = styled.span`
   font-variant-numeric: tabular-nums;
`;

const Note = styled.p`
   margin: 0;
   font-size: 11px;
   line-height: 1.5;
   color: ${colors.GRAY4};
`;
